import axios from 'axios'

import { store } from '../stores'
import { setVersionInfo } from '../actions/appState'

import networkConnected from './networkConnected'

import { bridge_updateCFDVersion } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { setUpdateAvailable } from '../VNUpdateManager/ActionCreators'
import { isTestMode } from '../VNAndroidSDK/Utils'

import createDataDogLogNoHistory from './datadog'

export const APP_VALIDATION_INTERVAL = 5 * 60 * 1000

const getRemoveVersion = (data) => {
  const versionRegex = /((v)?\d+(\.\d+)*(-[a-z0-9.-]+)?)/
  const version = data?.match?.(versionRegex)
  return version?.[0] || data?.trim()
}

/**
 * Determines if the app version has changed by comparing the local version with the remote version.
 * To test locally, set the local version to 'local' (e.g. `REACT_APP_VERSION=local yarn start`) to bypass update checks.
 *
 * @param {string | undefined} remoteVersion
 * @returns {[boolean, string | undefined]}
 */
const versionDidChange = (remoteVersion) => {
  const localVersion = process.env.REACT_APP_VERSION
  // local app version bypasses app update
  if (localVersion === 'local' || localVersion === 'development') {
    return [false, 'local']
  }
  // empty/null remote version does not trigger update if matches local
  if (!remoteVersion && !localVersion) {
    return [false, remoteVersion]
  }
  return [remoteVersion !== localVersion, remoteVersion]
}

export const refreshCache = () => {
  networkConnected()
    .then(() => {
      // need to tell the CFD to update its version, we really don't care if there isn't a CFD attached
      // since if there is nothing, the bridge just silently fails
      bridge_updateCFDVersion()

      window.location = `${window.location.origin}/?cacheBuster=${new Date().toISOString()}`
    })
    .catch(() => {
      console.error('Error refreshing cache: check your network connectivity.')
    })
}

export const newAppVersionAvailable = () => {
  return new Promise((resolve, reject) => {
    axios
      .get('/version.txt', {
        headers: { 'Cache-Control': 'no-cache' },
      })
      .then((response) => {
        resolve(versionDidChange(getRemoveVersion(response?.data)))
      })
      .catch((err) => {
        reject(err)
      })
  })
}

export const checkForNewAppVersion = async () => {
  try {
    if (isTestMode()) {
      createDataDogLogNoHistory('info', 'Running in test mode, skipping version check.', {})
      return false
    }
    const [hasNewVersion, remoteVersion] = await newAppVersionAvailable()
    if (hasNewVersion) {
      store.dispatch(setUpdateAvailable(true))
      store.dispatch(setVersionInfo({ remoteVersion }))
      createDataDogLogNoHistory('info', `New app version available: ${remoteVersion}`, {
        remoteVersion,
      })
      return true
    }
    return false
  } catch (err) {
    createDataDogLogNoHistory('info', 'error fetching version: check your network connectivity.', {
      err,
    })
    return false
  }
}

// When the automatic update timer commences, if the user does not choose to update
// immediately or the delayed morning update option within 30 minutes, the pos will
// perform a safe update on the next encounter of the concessions screen
// or the attract loop screen.
export const configureNewAppVersionAvailable = () => {
  return setInterval(checkForNewAppVersion, APP_VALIDATION_INTERVAL)
}
